import { memo } from 'react'
import { Text } from 'common-components'
import DotsLoading from 'common-components/DotsLoading'
import SpinningLoader from 'common-components/SpinningLoader'
import { COLORS } from 'consts/colors'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { useVoicePreview } from './useVoicePreview'
import * as Styled from './styled'

// Progress ring geometry (drawn inside the 36px play button).
const RING_RADIUS = 16.5
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

interface IProps {
  name: string
  url: string
  onRemove: () => void
}

// The attached voice-clone file: play preview + file name + detach (the file
// itself stays in My Files).
const VoiceCard: React.FC<IProps> = ({ name, url, onRemove }) => {
  const { playState, progress, toggle } = useVoicePreview(url)

  return (
    <Styled.Card data-testid="voice-clone-card">
      <Styled.PlayButton
        type="button"
        onClick={toggle}
        data-testid="voice-clone-play-button"
      >
        {playState === 'loading' ? (
          <SpinningLoader
            loaderColor={COLORS.primaryDark}
            size={16}
            wrapperSize={18}
            strokeWidth={2.5}
          />
        ) : playState === 'playing' ? (
          <>
            <Styled.ProgressRing viewBox="0 0 36 36">
              <Styled.ProgressTrack cx={18} cy={18} r={RING_RADIUS} />
              <Styled.ProgressIndicator
                cx={18}
                cy={18}
                r={RING_RADIUS}
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
              />
            </Styled.ProgressRing>
            <Styled.StopSquare />
          </>
        ) : (
          <Styled.PlayIcon />
        )}
      </Styled.PlayButton>

      <Styled.NameRow>
        <Text type="h4" fontWeight="600" color="secondaryExtraDark" ellipsis>
          {name}
        </Text>
      </Styled.NameRow>

      <Styled.RemoveButton
        type="button"
        onClick={onRemove}
        data-testid="voice-clone-remove-button"
      >
        <Styled.CloseIcon />
      </Styled.RemoveButton>
    </Styled.Card>
  )
}

// Shown while the voice file is being uploaded.
const UploadingCard: React.FC = () => (
  <Styled.Card data-testid="voice-clone-uploading-card">
    <Styled.PlayButton as="div" $disabled>
      <SpinningLoader
        loaderColor={COLORS.primaryDark}
        size={16}
        wrapperSize={18}
        strokeWidth={2.5}
      />
    </Styled.PlayButton>
    <Styled.NameRow>
      <Text type="h4" fontWeight="600" color="secondaryExtraDark" ellipsis>
        {translate(T.UPLOADING_YOUR_VOICE_LABEL)}
      </Text>
      <DotsLoading size="xxSmall" withoutHeight transparent />
    </Styled.NameRow>
  </Styled.Card>
)

export const VoiceCardUploading = memo(UploadingCard)

export default memo(VoiceCard)
